<template>
  <admin-wrapper
    :straight-title="additionalModuleName"
    :module-enabler="moduleEnabler"
    :inputs="inputs"
    :has-buttons="false"
    :is-form-fetched="isFormFetched"
  >
    <template #[`table.input`]>
      <v-row no-gutters class="mt-8">
        <v-col>
          <v-row no-gutters class="pb-4">
            <v-col cols="8">
              <v-row no-gutters align="baseline">
                <v-col cols="auto" class="pr-4 font-weight-bold">
                  {{ $i18n.t('admin.searchObjects.searchConfiguration') }}
                </v-col>
                <v-col cols="auto">
                  <base-rounded-button
                    icon="mdi-plus"
                    size="x-small"
                    color="success"
                    :tooltip="$i18n.t('admin.searchObjects.addSearchConfiguration')"
                    @click="openConfigurationSearchDialog(false, {})"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="9" style="border: 1px solid rgba(0, 0, 0, 0.12);">
              <v-data-table hide-default-footer :items="searchEngines" :headers="headers">
                <template #[`item.actions`]="{ item }">
                  <dots-menu
                    :items="dotsMenuItems"
                    @edit="openConfigurationSearchDialog(true, item)"
                    @delete="openDeleteConfigurationDialog(item)"
                  ></dots-menu>
                </template>
                <template #[`item.layer`]="{ item }">
                  <span>{{ layers[item.layer].name }}</span>
                </template>
                <template #[`item.attribute`]="{ item }">
                  <span>{{ getAttributeName(item) }}</span>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <main-input-dialog
        v-if="isConfigurationDialogLoaded"
        :is-visible.sync="isConfigurationDialogVisible"
        :title-translation="
          isConfigurationDialogEditing
            ? $i18n.t('admin.searchObjects.editSearchTitle')
            : $i18n.t('admin.searchObjects.addSearchTitle')
        "
        dialog-width="40%"
        :agree-text="isConfigurationDialogEditing ? 'dialog.agreeSave' : 'dialog.agreeAdd'"
        :inputs="configurationDialogInputs"
        :loading="isConfigurationDialogLoading"
        @agree="handleAddEditSearchEngine"
      >
        <template
          v-for="(input, idx) of configurationDialogInputs.filter(element => element.dataType)"
          #[`${input.name}.input`]="data"
        >
          <v-col cols="5" :key="idx">
            <data-input v-bind="data" v-model="currentSearchEngine[data.name]" />
          </v-col>
        </template>
      </main-input-dialog>
      <main-dialog
        :is-visible.sync="isDeleteConfigurationDialogVisible"
        :loading="isDeleteConfigurationDialogLoading"
        :title="$i18n.t('admin.searchObjects.deleteSearchTitle')"
        :body="$i18n.t('admin.searchObjects.deleteSearchBody', { name: currentSearchEngine.name })"
        @agree="deleteConfiguration"
        agree-text="dialog.agreeDelete"
      />
    </template>
  </admin-wrapper>
</template>
<script>
import { get, call } from 'vuex-pathify';
import AdminWrapper from '@/components/AdminWrapper';
import DotsMenu from '@/components/DotsMenu';
import DataInput from '@/components/DataInput';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'SearchObjectsSettings',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    AdminWrapper,
    DotsMenu,
    DataInput,
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainDialog: () => import('@/components/MainDialog'),
  },
  data: () => ({
    isFormFetched: false,
    dotsMenuItems: [
      {
        name: 'edit',
      },
      {
        name: 'delete',
      },
    ],
    isDeleteConfigurationDialogVisible: false,
    isDeleteConfigurationDialogLoading: false,
    isConfigurationDialogLoaded: false,
    isConfigurationDialogVisible: false,
    isConfigurationDialogEditing: false,
    isConfigurationDialogLoading: false,
    layerAttributes: [],
    currentSearchEngine: {},
  }),
  computed: {
    additionalModuleName: get('admin/additionalModules.SEARCH_OBJECTS_MODULE.verbose_name'),
    layers: get('layers/layers'),
    isModuleEnabled: get('admin/settingsValues@search_objects_module_enabled'),
    searchEngines: get('searchObjects/searchEngines'),
    moduleEnabler() {
      return {
        name: 'search_objects_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
    configurationDialogInputs() {
      return [
        {
          name: 'name',
          translationPath: 'admin.searchObjects.searchName',
          subtitle: 'admin.searchObjects.searchNameSubtitle',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'layer',
          translationPath: 'admin.searchObjects.searchLayer',
          subtitle: 'admin.searchObjects.searchLayerSubtitle',
          cols: '12',
          itemText: 'name',
          itemValue: 'id',
          items: Object.values(this.layers),
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
        },
        {
          name: 'attribute',
          translationPath: 'admin.searchObjects.searchAttribute',
          subtitle: 'admin.searchObjects.searchAttributeSubtitle',
          cols: '12',
          items: this.layerAttributes,
          itemText: 'label',
          itemValue: 'attribute',
          disabled: !this.currentSearchEngine.layer,
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
        },
      ];
    },
    inputs() {
      return [
        {
          name: 'table',
          hasSuffix: false,
          isCustom: true,
          isNamedSlot: false,
        },
      ];
    },
    headers() {
      return [
        {
          text: this.$i18n.t('admin.searchObjects.searchName'),
          value: 'name',
          sortable: false,
          align: 'center',
        },
        {
          text: this.$i18n.t('admin.searchObjects.searchLayer'),
          value: 'layer',
          sortable: false,
          align: 'center',
        },
        {
          text: this.$i18n.t('admin.searchObjects.searchAttribute'),
          value: 'attribute',
          sortable: false,
          align: 'center',
        },
        { text: '', value: 'actions', sortable: false },
      ];
    },
  },
  methods: {
    getLayers: call('layers/getLayers'),
    getSearchEngines: call('searchObjects/getSearchEngines'),
    addSearchEngine: call('searchObjects/addSearchEngine'),
    deleteSearchEngine: call('searchObjects/deleteSearchEngine'),
    editSearchEngine: call('searchObjects/editSearchEngine'),
    async init() {
      await Promise.all([this.getLayers(), this.getSearchEngines()]);
      this.isFormFetched = true;
    },
    async handleAddEditSearchEngine() {
      this.isConfigurationDialogLoading = true;
      let payload = this.currentSearchEngine;
      const engineId = payload.id;
      delete payload.id;
      try {
        if (this.isConfigurationDialogEditing) {
          await this.editSearchEngine({
            engineId,
            payload,
          });
        } else {
          await this.addSearchEngine(payload);
        }
      } finally {
        this.isConfigurationDialogLoading = false;
        this.isConfigurationDialogVisible = false;
      }
    },
    async deleteConfiguration() {
      this.isDeleteConfigurationDialogLoading = true;
      try {
        await this.deleteSearchEngine(this.currentSearchEngine.id);
      } finally {
        this.isDeleteConfigurationDialogLoading = false;
        this.isDeleteConfigurationDialogVisible = false;
      }
    },
    openDeleteConfigurationDialog(item) {
      this.currentSearchEngine.id = item.id;
      this.currentSearchEngine.name = item.name;
      this.isDeleteConfigurationDialogVisible = true;
    },
    openConfigurationSearchDialog(isEdit = false, data) {
      this.isConfigurationDialogEditing = isEdit;
      this.isConfigurationDialogLoaded = true;
      this.isConfigurationDialogVisible = true;
      this.currentSearchEngine = JSON.parse(JSON.stringify(data));
    },
    getLayerAttributes(layerId) {
      return this.$_flattenStructure(this.layers[layerId]?.form_schema?.elements || []);
    },
    getAttributeName(data) {
      const { layer, attribute } = data;
      return this.getLayerAttributes(layer).find(attr => attr.attribute === attribute)?.label || attribute;
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) {
        this.isFormFetched = false;
        this.init();
      }
    },
    'currentSearchEngine.layer': function(nV, oV) {
      if (oV && oV !== nV) this.currentSearchEngine.attribute = null;
      if (nV) this.layerAttributes = this.getLayerAttributes(nV);
    },
  },
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFormFetched = true;
    }
  },
};
</script>
